import { Pipe, PipeTransform } from '@angular/core';

/**
 * Produces a `Date` safe for `DatePipe`, or `null` so templates can fall back to the raw API string.
 * Handles RTL marks, Arabic slashes, and pre-formatted Hijri strings (e.g. "17‏/11‏/1447") without throwing.
 */
@Pipe({
  name: 'fixAnyDate',
})
export class FixAnyDatePipe implements PipeTransform {
  transform(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      const normalized = this.normalizeDateString(value);
      const slashParts = normalized
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      if (slashParts.length === 3) {
        const nums = slashParts.map((p) => this.parseLeadingInt(p));
        if (nums.every((n) => n !== null && !isNaN(n))) {
          const [a, b, c] = nums as [number, number, number];
          let day: number;
          let month: number;
          let year: number;

          if (c >= 1000) {
            day = a;
            month = b;
            year = c;
          } else if (a >= 1000) {
            year = a;
            month = b;
            day = c;
          } else {
            day = a;
            month = b;
            year = c;
          }

          if (month < 1 || month > 12 || day < 1 || day > 31) {
            return null;
          }

          /** Server often sends Hijri calendar strings; don't coerce to a misleading Gregorian `Date`. */
          if (year >= 1200 && year <= 1700) {
            return null;
          }

          const d = new Date(year, month - 1, day);
          return isNaN(d.getTime()) ? null : d;
        }
      }

      const parsed = new Date(normalized);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    const d = new Date(value as string | number);
    return isNaN(d.getTime()) ? null : d;
  }

  private normalizeDateString(s: string): string {
    let t = s.replace(/[\u200e\u200f\u061c\u202a-\u202e]/g, '');
    t = t.replace(/\u066b/g, '.');
    t = t.replace(/[\u2215\u2044\u29f8／﹨､]/g, '/');

    const westernArabic = '٠١٢٣٤٥٦٧٨٩';
    const latin = '0123456789';
    for (let i = 0; i < 10; i++) {
      t = t.split(westernArabic[i]).join(latin[i]);
    }
    for (let i = 0; i < 10; i++) {
      const ch = String.fromCharCode(0x06f0 + i);
      t = t.split(ch).join(latin[i]);
    }

    return t.trim();
  }

  /** First contiguous integer in a segment (stops at RTL marks / letters). */
  private parseLeadingInt(segment: string): number | null {
    const m = segment.match(/-?\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return isNaN(n) ? null : n;
  }
}
